import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCurrentClient, useCurrentUser } from '@/features/auth/state/auth'

import { useFetchSignatureById } from '@/features/signatures/services'

import { getCleanDomain } from '@/utils/getCleanDomain'
import { useUpdateAgreement } from '../services/useUpdateAgreement'
import { useFetchAgreementById } from '../services/useFetchAgreementById'
import { ROUTE_BUILDERS, ROUTES } from '@/config/routes'
import { useFetchHistoryAgreementById } from '../services/useFetchHistoryAgreementById'
import { useApplyEditAgreement } from '../services/useApplyEditAgreement'
import { cleanHtmlContent } from '../utils/cleanHtmlContent'
import { getDomainIcon } from '../utils/domainIcon'

export type Anchor = { start: string | RegExp; end: string | RegExp }

export default function useMinuta() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useCurrentUser()
  const client = useCurrentClient()

  const [currentPage, setCurrentPage] = useState(1)
  const [isSpeaking, setIsSpeaking] = useState(false)

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { data: agreementData, error: agreementError, isLoading: isLoadingAgreement } = useFetchAgreementById(id || '')
  const {
    data: signatureStatus,
    error: signatureError,
    isLoading: isLoadingSignature,
    isFetching
  } = useFetchSignatureById(id || '')
  const { mutate: updateAgreement } = useUpdateAgreement(id || '')

  const agreementIdForHistory = agreementData?.id // pode ser undefined enquanto carrega

  const { data: historySuggestions = [], isLoading: isLoadingHistorySuggestions } = useFetchHistoryAgreementById(
    agreementIdForHistory ?? ''
  )
  const { mutate: applyEdit } = useApplyEditAgreement()
  // Cancela qualquer síntese quando muda de página
  useEffect(() => {
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }, [currentPage])

  // Autoatualiza quando both testemunhas assinaram

  useEffect(() => {
    if (
      !signatureStatus?.data?.isModifica &&
      // agreementData?.isAssinado &&
      agreementData?.isFrist &&
      agreementData?.status === 'ANALISE_CONCLUIDA'
    ) {
      handleUpdateAgreement()
    }
  }, [signatureStatus, agreementData])

  useEffect(() => {
    if (!client || !id) return
    if (signatureStatus?.data?.isModifica === false) return

    navigate(ROUTE_BUILDERS.signContract(id), { replace: true })
  }, [client, id, navigate, signatureStatus?.data?.isModifica])

  const htmlContent = typeof agreementData?.html === 'string' ? agreementData.html : ''

  const fullStyledHtml = cleanHtmlContent(htmlContent)

  const editableHtml = htmlContent

  const shouldShowLoading = useMemo(() => {
    if (isLoadingAgreement || isLoadingSignature || isLoadingHistorySuggestions) return true
    if (isFetching && signatureStatus?.data?.isModifica) return true
    return false
  }, [isLoadingAgreement, isLoadingSignature, isLoadingHistorySuggestions, isFetching, signatureStatus])

  const domain = getCleanDomain()
  const selectedIcon = getDomainIcon(domain)

  const displaySignatureButton = signatureStatus?.data?.isModifica !== false
  const displaySignatureIsVerifedButton =
    signatureStatus?.data?.isModifica === false && signatureStatus?.data?.isVerifed === true
  const canCloseContract =
    displaySignatureButton === false &&
    Boolean(client) &&
    (signatureStatus?.data?.isVerifed === true || agreementData?.status === 'PENDENTE') &&
    Boolean(agreementData?.witnessesComplete || agreementData?.isTermoWitness)
  const shouldContinueWitnessFlow =
    displaySignatureButton === false &&
    Boolean(client) &&
    !agreementData?.isAssinado &&
    !agreementData?.witnessesComplete &&
    !agreementData?.isTermoWitness
  const nextWitnessSlot = signatureStatus?.data?.check_testemunha_1 ? 2 : 1

  const handleSpeak = () => {
    if (isSpeaking) return
    const utterance = new SpeechSynthesisUtterance()
    utterance.text = fullStyledHtml.replace(/<[^>]+>/g, '')
    utterance.lang = 'pt-BR'
    utterance.rate = 1
    utterance.onend = () => setIsSpeaking(false)

    setIsSpeaking(true)
    window.speechSynthesis.speak(utterance)
  }

  const handleStop = () => {
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
  }

  const handleUpdateAgreement = async () => {
    try {
      await new Promise<void>((resolve, reject) => {
        updateAgreement(undefined, {
          onSuccess: () => {
            setMessage({ type: 'success', text: 'Contrato atualizado com sucesso!' })
            setIsDialogOpen(true)
            resolve()
          },
          onError: err => {
            setMessage({ type: 'error', text: `Erro ao atualizar contrato: ${err}` })
            setIsDialogOpen(true)
            reject(err)
          }
        })
      })
    } catch (err) {
      console.error(err)
    }
  }
  const handleSave = async (cleanHtml: string, id_suggestion: string) => {
    // Reconstrói HTML completo mantendo segmentos congelados
    applyEdit(
      { id_edit: id_suggestion, html: cleanHtml },
      {
        onSuccess: () => {
          setMessage({ type: 'success', text: 'Edição do contrato salva com sucesso!' })
          setIsDialogOpen(true)
        },
        onError: err => {
          setMessage({ type: 'error', text: `Erro ao salvar edição do contrato: ${err}` })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const handleClient = () => {
    // Sem cliente autenticado, a entrada correta do fluxo público é o login do cliente.
    if (!client) {
      navigate(ROUTE_BUILDERS.loginClientMinuta(id!))
      return
    }

    navigate(ROUTE_BUILDERS.signContract(id!))
  }

  const handleBack = () => {
    if (client) {
      navigate(ROUTES.CLIENT_HOME.path)
    } else if (user?.typeUser === 'USUARIO') {
      navigate(ROUTES.DASHBOARD_HOME.path)
    } else if (user?.typeUser === 'ADMIN') {
      navigate(-1)
    }
  }

  return {
    id,
    user,
    client,
    agreementData,
    historySuggestions,
    isSpeaking,
    message,
    isDialogOpen,
    currentPage,
    setCurrentPage,
    fullStyledHtml,
    editableHtml,
    shouldShowLoading,
    selectedIcon,
    handleUpdateAgreement,
    displaySignatureButton,
    displaySignatureIsVerifedButton,
    canCloseContract,
    shouldContinueWitnessFlow,
    nextWitnessSlot,
    handleSpeak,
    handleStop,
    handleSave,
    handleClient,
    handleBack,
    setIsDialogOpen,
    agreementError,
    signatureError
  }
}
